import { useMemo } from 'react'
import useInternalUserPermissions from './useInternalUserPermissions'
import {
  ApplicantIdentity,
  maskApplicantInHtml,
  maskApplicantInText,
  maskEmail,
  maskName,
  maskPhone,
} from '../utils/applicantPrivacy'

interface ApplicantPrivacy {
  isMasked: boolean
  maskName: (value?: string | null) => string
  maskEmail: (value?: string | null) => string
  maskPhone: (value?: string | null) => string
  maskText: (text: string | null | undefined, applicant: ApplicantIdentity) => string
  maskHtml: (html: string | null | undefined, applicant: ApplicantIdentity) => string
}

const passThrough = (value?: string | null) => value || ''

/**
 * Internal users (admin, internal user, limited internal user) see applicants' names, emails
 * and phone numbers masked. Customers and resellers see their own applicants in clear text,
 * so every function here is a pass-through for them.
 */
const useApplicantPrivacy = (): ApplicantPrivacy => {
  const { isInternalUser } = useInternalUserPermissions()

  return useMemo(
    () =>
      isInternalUser
        ? {
            isMasked: true,
            maskName,
            maskEmail,
            maskPhone,
            maskText: maskApplicantInText,
            maskHtml: maskApplicantInHtml,
          }
        : {
            isMasked: false,
            maskName: passThrough,
            maskEmail: passThrough,
            maskPhone: passThrough,
            maskText: passThrough,
            maskHtml: passThrough,
          },
    [isInternalUser]
  )
}

export default useApplicantPrivacy
